import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { t } from 'i18next';
import _ from 'lodash';
import { formatTime } from '@/datetime/dateTime.utilities';
import { KpiHeaderWithDescription } from '@/impactReports/cellRenderers/KpiHeaderWithDescription.atom';
import { UseCaseNameCellRenderer } from '@/impactReports/cellRenderers/UseCaseNameCellRenderer.atom';
import { UseCaseStatusCellRenderer } from '@/impactReports/cellRenderers/UseCaseStatusCellRenderer.atom';
import {
  IMPACT_OWNER_FIRSTNAME,
  IMPACT_OWNER_LASTNAME,
  IMPACT_REPORT_CATEGORY_DISPLAY_NAME,
  IMPACT_REPORT_LABEL_ID,
  MANAGER_FIRSTNAME,
  MANAGER_LASTNAME,
  REVIEWED_BY_FIRSTNAME,
  REVIEWED_BY_LASTNAME,
  quantifyUnits,
} from '@/impactReports/impactReport.types';
import {
  formatFrequency,
  formatNumberAsValue,
  getCategoryDisplayString,
  getCustomCategoryTitle,
  getImpactTypeDisplayString,
  getKPIColumnNameFromExpectedAnnual,
  getKPIDisplayName,
  getKPIItemColumnName,
  getKPIUnit,
  getStageDisplayString,
  isKPIColumnName,
  isKPIExpectedAnnualColumnName,
} from '@/impactReports/impactReportDisplay.utilities';
import { MAX_TABLE_ROWS } from '@/main/app.constants';
import { getSupportedUnits } from '@/services/systemConfiguration.utilities';
import { validateGuid } from '@/utilities/guid.utilities';
import { logError } from '@/utilities/logger';
import { sqFormulasApi } from '@seeqdev/api/endpoint/FormulasApi';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqTablesApi } from '@seeqdev/api/endpoint/TablesApi';
import { OperationEnum } from '@seeqdev/api/model/CompositeFilter';
import { FilterTypeEnum } from '@seeqdev/api/model/ValueFilter';
import type {
  ColDef,
  Column,
  ColumnState,
  GridApi,
  IRowNode,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import type { ColumnConfig, ImpactReportCategory, ImpactReportUseCase } from '@/impactReports/impactReport.types';
import type { ColumnDefinitionOutputV1 } from '@seeqdev/api/model/ColumnDefinitionOutputV1';
import type { PropertiesForItemUUIDColumn } from '@seeqdev/api/model/models';

export function getColumnDefinitions(
  categories: ImpactReportCategory[] | undefined,
  showSite: boolean,
  showAssetPath: boolean,
  showLocation: boolean,
  timezone: { name: string },
  kpiColumns: ColumnDefinitionOutputV1[] = [],
  expectedAnnualKpiColumns: ColumnDefinitionOutputV1[] = [],
  expectedAnnualKpiVisibility: Record<string, boolean> = {},
): ColDef[] {
  const buildKpiColumnDef = (columnName: string, headerName: string, unit: string, description?: string | null) => ({
    field: columnName,
    headerName,
    headerComponent: KpiHeaderWithDescription,
    headerComponentParams: { description },
    flex: 1,
    cellClass: 'right-align',
    headerClass: 'pull-right',
    floatingFilter: true,
    filter: 'agSetColumnFilter',
    aggFunc: 'sum',
    chartDataType: 'series' as const,
    valueFormatter: (params: ValueFormatterParams) => {
      if (!params.value) return '';
      const formatterFn = getValueFormatter(unit);
      return params.value ? formatterFn(params.value, unit) : '';
    },
    valueGetter: (rowData: ValueGetterParams<string, string>) => {
      if (!rowData || !rowData.data) return;
      const value = _.get(rowData.data, columnName);
      const number = Number(value);
      return isNaN(number) ? 0 : Math.round(number);
    },
  });

  const categoryCustomColumns = categories
    ? categories.map((category: ImpactReportCategory) => {
        const cat = category.category as any;
        const fieldName = getCustomCategoryTitle(category);
        const options = category.options;
        return {
          field: cat.id,
          headerName: fieldName,
          flex: 1,
          floatingFilter: true,
          filter: 'agSetColumnFilter',
          chartDataType: 'category' as const,
          valueGetter: (rowData: ValueGetterParams<string, string>) => {
            if (!rowData || !rowData.data) return;
            return getCategoryDisplayString(rowData.data[cat.id], options, rowData.data[cat.id]);
          },
          context: { canAggregateBy: true },
        };
      })
    : [];

  const expectedAnnualColumnsByBase = new Map(
    expectedAnnualKpiColumns.map((column) => [getKPIColumnNameFromExpectedAnnual(column.columnName), column]),
  );

  const kpiCustomColumns = kpiColumns.flatMap(({ columnName, description }) => {
    const unit = getKPIUnit(columnName);
    const displayName = getKPIDisplayName(columnName);
    const baseColumn = buildKpiColumnDef(columnName, displayName, unit, description);

    const expectedAnnualColumn = expectedAnnualColumnsByBase.get(columnName);
    if (!expectedAnnualColumn || !expectedAnnualKpiVisibility[columnName]) {
      return [baseColumn];
    }

    const expectedAnnualDisplayName = `${displayName} - ${t('IMPACT_REPORT.EXPECTED_ANNUAL_SAVINGS')}`;
    const expectedAnnualColumnDef = buildKpiColumnDef(
      expectedAnnualColumn.columnName,
      expectedAnnualDisplayName,
      unit,
      description,
    );

    return [baseColumn, expectedAnnualColumnDef];
  });

  const assetColumn = {
    field: SeeqNames.ImpactReport.ImpactAsset,
    headerName: t('IMPACT_REPORT.TABLE_HEADERS.ASSET'),
    flex: 1,
    floatingFilter: true,
    filter: 'agTextColumnFilter',
    aggFunc: 'first',
    chartDataType: 'category' as const,
    context: { canAggregateBy: true },
  };

  const siteColumn = {
    field: SeeqNames.ImpactReport.ImpactSite,
    headerName: t('IMPACT_REPORT.TABLE_HEADERS.SITE'),
    flex: 1,
    floatingFilter: true,
    filter: 'agSetColumnFilter',
    aggFunc: 'first',
    chartDataType: 'category' as const,
    context: { canAggregateBy: true },
  };

  const locationColumn = {
    field: SeeqNames.ImpactReport.Location,
    headerName: t('IMPACT_REPORT.TABLE_HEADERS.LOCATION'),
    flex: 2,
    valueGetter: (rowData: ValueGetterParams<any, any, any>) => {
      if (!rowData || !rowData.data || !rowData.node) return;
      if (rowData.node.rowPinned === 'bottom') return '';
      return rowData.data.locationDisplay;
    },
    chartDataType: 'category' as const,
    floatingFilter: true,
    filter: 'agSetColumnFilter',
    context: { canAggregateBy: true },
  };

  const columnDefs: (ColDef | null)[] = [
    {
      field: 'value',
      headerName: '# of Reports',
      hide: true,
      suppressColumnsToolPanel: true,
      valueGetter: (_rowData) => 1,
    },
    {
      field: SeeqNames.ImpactReport.ImpactName,
      headerName: t('IMPACT_REPORT.TABLE_HEADERS.NAME'),
      flex: 2,
      floatingFilter: true,
      filter: 'agTextColumnFilter',
      filterValueGetter: (params: ValueGetterParams) => `${params.data[SeeqNames.ImpactReport.ImpactName]}`,
      cellRenderer: UseCaseNameCellRenderer,
      valueGetter: (rowData) => {
        if (!rowData || !rowData.data) return;
        return {
          name: rowData.data[SeeqNames.ImpactReport.ImpactName],
          worksheetId: rowData.data[SeeqNames.ImpactReport.AssignedToWorksheet],
          workbookId: rowData.data[SeeqNames.ImpactReport.AssignedToWorkbook],
          type: rowData.data[SeeqNames.ImpactReport.ImpactBasedOn],
        };
      },
    },
    {
      field: SeeqNames.ImpactReport.ImpactStatus,
      headerName: t('IMPACT_REPORT.TABLE_HEADERS.STATUS'),
      flex: 1,
      floatingFilter: true,
      filter: 'agSetColumnFilter',
      chartDataType: 'category' as const,
      filterParams: {
        valueFormatter: (params: any) => {
          return getStageDisplayString(params.value);
        },
      },
      valueGetter: (rowData) => {
        if (!rowData || !rowData.data) return;
        return rowData.data[SeeqNames.ImpactReport.ImpactStatus]?.toLowerCase().trim();
      },
      cellRenderer: UseCaseStatusCellRenderer,
      context: { canAggregateBy: true },
    },
    ...categoryCustomColumns,
    showAssetPath ? assetColumn : null,
    showSite ? siteColumn : null,
    showLocation ? locationColumn : null,
    {
      field: SeeqNames.ImpactReport.ImpactOwner,
      headerName: t('IMPACT_REPORT.TABLE_HEADERS.OWNER'),
      filter: 'agSetColumnFilter',
      valueGetter: (rowData) => {
        if (!rowData || !rowData.data) return;
        return `${rowData.data[IMPACT_OWNER_FIRSTNAME] ?? ''} ${rowData.data[IMPACT_OWNER_LASTNAME] ?? ''}`;
      },
      context: { canAggregateBy: true },
    },
    {
      field: SeeqNames.ImpactReport.Manager,
      filter: 'agSetColumnFilter',
      headerName: t('IMPACT_REPORT.TABLE_HEADERS.MANAGER'),
      flex: 2,
      valueGetter: (rowData) => {
        if (!rowData || !rowData.data || !rowData.node) return;
        if (rowData.node.rowPinned === 'bottom') return '';
        const first = rowData.data[MANAGER_FIRSTNAME];
        const last = rowData.data[MANAGER_LASTNAME];
        return `${first ?? ''} ${last ?? ''}`;
      },
      chartDataType: 'category' as const,
      aggFunc: 'first',
      context: { canAggregateBy: true },
    },
    {
      field: SeeqNames.ImpactReport.ReviewedBy,
      headerName: t('IMPACT_REPORT.TABLE_HEADERS.REVIEWED_BY'),
      filter: 'agSetColumnFilter',
      flex: 2,
      valueGetter: (rowData) => {
        if (!rowData || !rowData.data || !rowData.node) return;
        if (rowData.node.rowPinned === 'bottom') return '';
        const first = rowData.data[REVIEWED_BY_FIRSTNAME];
        const last = rowData.data[REVIEWED_BY_LASTNAME];
        const fullName = `${first ?? ''} ${last ?? ''}`.trim();
        return fullName || rowData.data[SeeqNames.ImpactReport.ReviewedBy] || '';
      },
      aggFunc: 'first',
      chartDataType: 'category' as const,
    },
    {
      field: SeeqNames.ImpactReport.ReviewDate,
      headerName: t('IMPACT_REPORT.TABLE_HEADERS.REVIEW_DATE'),
      flex: 1,
      valueGetter: (rowData) => {
        if (!rowData || !rowData.data || !rowData.node) return;
        if (rowData.node.rowPinned === 'bottom') return '';
        const reviewDate = rowData.data[SeeqNames.ImpactReport.ReviewDate];
        return reviewDate ? formatTime(reviewDate, timezone, 'l') : '';
      },
    },
    {
      field: SeeqNames.ImpactReport.ReviewComment,
      headerName: t('IMPACT_REPORT.TABLE_HEADERS.REVIEW_COMMENT'),
      flex: 2,
      floatingFilter: true,
      filter: 'agTextColumnFilter',
    },
    {
      field: SeeqNames.ImpactReport.ImpactSavingsType,
      headerName: t('IMPACT_REPORT.TABLE_HEADERS.TYPE'),
      flex: 2,
      valueGetter: (rowData) => {
        if (!rowData || !rowData.data || !rowData.node) return;
        if (rowData.node.rowPinned === 'bottom') return '';
        return getImpactTypeDisplayString(rowData.data[SeeqNames.ImpactReport.ImpactSavingsType]);
      },
      chartDataType: 'category' as const,
      floatingFilter: true,
      filter: 'agSetColumnFilter',
      context: { canAggregateBy: true },
    },
    {
      field: SeeqNames.ImpactReport.ImpactStartDate,
      headerName: t('IMPACT_REPORT.TABLE_HEADERS.START'),
      flex: 1,
      valueGetter: (rowData) => {
        if (!rowData || !rowData.data || !rowData.node) return;
        if (rowData.node.rowPinned === 'bottom') return '';
        return formatTime(rowData.data[SeeqNames.ImpactReport.ImpactStartDate], timezone, 'l');
      },
    },
    {
      field: SeeqNames.ImpactReport.ImpactEndDate,
      headerName: t('IMPACT_REPORT.TABLE_HEADERS.END'),
      flex: 1,
      valueGetter: (rowData) => {
        if (!rowData || !rowData.data || !rowData.node) return;
        if (rowData.node.rowPinned === 'bottom') return '';
        return formatTime(rowData.data[SeeqNames.ImpactReport.ImpactEndDate], timezone, 'l');
      },
    },
    {
      field: SeeqNames.ImpactReport.ImpactFrequency,
      headerName: t('IMPACT_REPORT.TABLE_HEADERS.FREQUENCY'),
      flex: 1,
      valueGetter: (rowData) => {
        if (!rowData || !rowData.data || !rowData.node) return;
        if (rowData.node.rowPinned === 'bottom') return '';
        const frequency = rowData.data[SeeqNames.ImpactReport.ImpactFrequency];
        if (!frequency) return '';
        return formatFrequency(frequency);
      },
    },
    ...kpiCustomColumns,
  ];
  return columnDefs.filter(Boolean).map((colDef) => ({ ...colDef, wrapHeaderText: true, autoHeaderHeight: true }));
}
// determine the formula to be used to calculate the impact values (monetary as well as custom kpi)
export async function getImpactFormula(
  rowData: ImpactReportUseCase,
  lookupColumn: string,
  displayStartDate: string,
  displayEndDate: string,
): Promise<string | undefined> {
  if (!rowData) return;

  const impactType = rowData[SeeqNames.ImpactReport.ImpactSavingsType];
  const impactFrequency = rowData[SeeqNames.ImpactReport.ImpactFrequency];
  const startDate = rowData[SeeqNames.ImpactReport.ImpactStartDate];
  const endDate = rowData[SeeqNames.ImpactReport.ImpactEndDate];

  // @ts-ignore
  const impactAmountOrItemId = rowData[lookupColumn];
  const validItem = validateGuid(impactAmountOrItemId);
  if (!startDate || !impactAmountOrItemId || (!impactFrequency && validItem)) {
    return undefined;
  }

  let formula;
  // first determine if we are dealing with a custom calculation - we determine that by checking the value of the
  // column and checking that it's a valid guid.
  if (validItem) {
    // we have to fetch the item to obtain its unit to ensure proper formula selection
    const { data } = await sqItemsApi.getProperty({
      id: impactAmountOrItemId,
      propertyName: SeeqNames.Properties.ValueUom,
    });
    const unit = data.value ?? '';

    const aggregationFn = unit.indexOf('/') < 0 ? 'sum()' : 'totalized()';
    if (!endDate) {
      formula = `0.tosignal().spliceDate($customSavingsSignal,'${startDate}').aggregate(${aggregationFn},capsule('${displayStartDate}', '${displayEndDate}'))`;
    } else {
      formula = `$customSavingsSignal.within(condition(capsule('${startDate}','${endDate}'))).aggregate(${aggregationFn},capsule('${displayStartDate}', '${displayEndDate}'))`;
    }
    return formula;
  } else if (impactType === 'continuous') {
    if (!endDate) {
      formula = `(condition(10y,capsule('${startDate}', now())).setUncertainty(1h)).intersect(days().setProperty('Impact Estimate',${impactAmountOrItemId}/${impactFrequency}),keepProperties())`;
    } else {
      formula = `(condition(10y,capsule('${startDate}','${endDate}'))).intersect(past()).intersect(days().setProperty('Impact Estimate',${impactAmountOrItemId}/${impactFrequency}),keepProperties()).setUncertainty(1h)`;
    }
  } else if (impactType === 'oneTime') {
    formula = `condition(10y,capsule('${startDate}', '${startDate}').setProperty('Impact Estimate', ${impactAmountOrItemId}))`;
  }

  return `(${formula}).toSignal('Impact Estimate').sum(capsule('${displayStartDate}','${displayEndDate}'))`;
}

/**
 * The impact is calculated based on a Formula that is stored on the use case.
 * Before we can calculate the impact, we need to make sure that we add the KPI columns to the table data so we can
 * reference user provided information on edit.
 */
export async function decorateMaterializedTableWithImpactValue(
  rawTableData: ImpactReportUseCase[],
  kpiColumns: ColumnDefinitionOutputV1[],
  startDate: string,
  endDate: string,
) {
  const tableData = rawTableData.map((item) => {
    const kpiItemColumns = Object.keys(item).filter((key) => isKPIColumnName(key));

    let result: ImpactReportUseCase = { ...item };

    // This is a bit hairy and probably not ideal but here's what's going on:
    // - custom KPI columns are stored in the item object with a prefix
    // - the values that are stored are the values that are entered by the user when they create the report
    // - the table needs to display the calculated values based on the formula that is stored on the use case (which
    // varies based on the type of impact)
    // - we need to ensure that the table contains the original user provided values so we can display those on edit
    // - the "flow of data" depends on if the data is viewed in the table or in the detail view, or from the worksheet
    // so we need to ensure we only add the user provided values if they are not already present (or we'd
    // artificially inflate things)
    kpiItemColumns.forEach((column) => {
      const calculatedValueColumnName = getKPIItemColumnName(column);
      if (!_.get(result, calculatedValueColumnName)) {
        result = {
          ...result,
          [getKPIItemColumnName(column)]: _.get(item, column),
        };
      }
    });

    return result;
  });

  return await Promise.all(
    tableData.map(async (row: any) => {
      await Promise.all(
        kpiColumns.map(async (kpiColumn) => {
          row[kpiColumn.columnName] = await calculateImpactValue(row, kpiColumn, startDate, endDate);
        }),
      );

      return row;
    }),
  );
}
export async function calculateImpactValue(
  row: Record<string, any>,
  kpiColumn: ColumnDefinitionOutputV1,
  startDate: string,
  endDate: string,
) {
  let formula;
  try {
    formula = await getImpactFormula(
      row as unknown as ImpactReportUseCase,
      getKPIItemColumnName(kpiColumn.columnName),
      startDate,
      endDate,
    );
  } catch (e) {
    logError(`Failed to get impact formula for ${kpiColumn.columnName}`);
    return;
  }
  if (formula) {
    try {
      const parameters = [];
      if (formula.indexOf('$customSavingsSignal') > -1) {
        parameters.push(`customSavingsSignal=${row[getKPIItemColumnName(kpiColumn.columnName)]}`);
      }
      const formulaResponse = await sqFormulasApi.runFormula({
        parameters,
        formula,
      });
      return formulaResponse.data?.scalar?.value;
    } catch (e) {
      logError(`Failed to calculate impact value for ${kpiColumn.columnName}`);
      row[kpiColumn.columnName] = 0; // fallback to 0 if the formula fails
    }
  }
}
/**
 * This returns the Materialized Table for the Impact Report table without the "likes" rows
 * this is accomplished by filtering to only the desired category context labels, not the "like" label.
 * In theory, we should be able to filter by category_id but that doesn't work yet
 * (https://seeq.atlassian.net/browse/CRAB-45806)
 *
 * The materialized table endpoint returns an array of headers and an array of rows where rows can be duplicated.
 * There will always be one row per assigned context label (that's why we filter out the "like" label), but there can
 * also be duplicates for no apparent reason :shrug:
 *
 * The AGGrid table expects context category ids to be provided as the column name, and the value renderer will
 * parse the context label id to display the label name.
 */
export async function getTableWithoutLikes(tableId: string, categories: ImpactReportCategory[]) {
  const allLabelIds = _.chain(categories)
    .map((category) => category.options)
    .map((options) => options?.map((opt) => opt.value))
    .flatten()
    .value();

  const properties: PropertiesForItemUUIDColumn[] = [
    {
      uuidColumn: SeeqNames.ImpactReport.ImpactOwner,
      propertyNames: [SeeqNames.Properties.FirstName, SeeqNames.Properties.LastName],
    },
    {
      uuidColumn: SeeqNames.ImpactReport.Manager,
      propertyNames: [SeeqNames.Properties.FirstName, SeeqNames.Properties.LastName],
    },
    {
      uuidColumn: SeeqNames.ImpactReport.ReviewedBy,
      propertyNames: [SeeqNames.Properties.FirstName, SeeqNames.Properties.LastName],
    },
  ];

  const { data } = await sqTablesApi.queryTableData(
    {
      limit: MAX_TABLE_ROWS,
      columnsToInclude: [],
      propertiesToInclude: properties,
    },
    { tableId },
  );

  const transformedData = transformTableResponseToObject(data.rows, data.headers);
  // we will now have multiple rows (each context category on its own row)
  const withLabels = mergeContextLabels(transformedData);
  return mergeRows(withLabels);
}

/**
 * Little helper function that creates an object with the column name as the key and the value as the value
 */
function transformTableResponseToObject(rows: any[], headers: Record<string, any>) {
  return rows.map((row) =>
    headers.reduce((obj: any, header: Record<string, any>, index: number) => {
      obj[header.name] = row[index] || null;
      return obj;
    }, {}),
  );
}

function mergeContextLabels(data: Record<string, any>[]) {
  return data.map((row) => {
    const categoryId = row['context_labels.category_id'];
    row[categoryId] = row['context_labels.label_id'];
    row[`${categoryId}_${IMPACT_REPORT_LABEL_ID}`] = row['context_labels.id'];
    row[`${IMPACT_REPORT_CATEGORY_DISPLAY_NAME}_${categoryId}`] = row['context_labels.label_name'];
    return row;
  });
}

function mergeRows(data: Record<string, any>[]) {
  return Object.values(
    data.reduce((acc, item) => {
      const key = `${item[SeeqNames.MaterializedTables.DatumIdColumn]}-${
        item[SeeqNames.MaterializedTables.ItemIdColumn]
      }`;
      if (!acc[key]) {
        acc[key] = {
          [SeeqNames.MaterializedTables.DatumIdColumn]: item[SeeqNames.MaterializedTables.DatumIdColumn],
          [SeeqNames.MaterializedTables.ItemIdColumn]: item[SeeqNames.MaterializedTables.ItemIdColumn],
        };
      }
      Object.assign(acc[key], item);
      return acc;
    }, {}),
  );
}

export async function getImpactReportByWorksheet(tableId: string, worksheetId: string, workbookId: string) {
  const properties: PropertiesForItemUUIDColumn[] = [
    {
      uuidColumn: SeeqNames.ImpactReport.ImpactOwner,
      propertyNames: [SeeqNames.Properties.FirstName, SeeqNames.Properties.LastName],
    },
    {
      uuidColumn: SeeqNames.ImpactReport.Manager,
      propertyNames: [SeeqNames.Properties.FirstName, SeeqNames.Properties.LastName],
    },
  ];
  const { data } = await sqTablesApi.queryTableData(
    {
      limit: MAX_TABLE_ROWS,
      columnsToInclude: [],
      propertiesToInclude: properties,
      filter: {
        compositeFilter: {
          filter1: {
            valueFilter: {
              columnName: SeeqNames.ImpactReport.AssignedToWorksheet,
              filterType: FilterTypeEnum.IN,
              value: { value: [worksheetId] },
            },
          },
          filter2: {
            valueFilter: {
              columnName: SeeqNames.ImpactReport.AssignedToWorkbook,
              filterType: FilterTypeEnum.IN,
              value: { value: [workbookId] },
            },
          },
          operation: OperationEnum.AND,
        },
      },
    },
    { tableId },
  );
  const transformedData = transformTableResponseToObject(data.rows, data.headers);
  // we will now have multiple rows (each context category on its own row)
  const withLabels = mergeContextLabels(transformedData);
  const mergedData = mergeRows(withLabels);
  // now w
  return mergedData[0];
}

export function exposeGeoData(tableData: ImpactReportUseCase[]) {
  return tableData.map((row) => {
    const location = row[SeeqNames.ImpactReport.Location];
    if (!location) return row;

    const { latitude, longitude, placeId, label } = JSON.parse(location);
    return {
      ...row,
      lat: latitude,
      long: longitude,
      placeId,
      locationDisplay: label,
    };
  });
}

export function rollupByLocation(api: GridApi) {
  const grouped: Record<string, any> = {};
  const kpiColumns = api.getColumnDefs()!.filter((col) => {
    const field = _.get(col, 'field', '');
    return isKPIColumnName(field) || isKPIExpectedAnnualColumnName(field);
  });
  api.forEachNodeAfterFilter((row: IRowNode) => {
    const { placeId, lat, long, locationDisplay } = row.data;
    if (!placeId) return;

    if (!grouped[placeId]) {
      grouped[placeId] = {
        placeId,
        lat,
        long,
        locationDisplay,
        reportCount: 0,
        ...Object.fromEntries(kpiColumns.map((kpi) => [_.get(kpi, 'field'), 0])),
      };
    }
    grouped[placeId].reportCount += 1;

    for (const kpi of kpiColumns) {
      grouped[placeId][_.get(kpi, 'field')] += Number(row.data[_.get(kpi, 'field')]) || 0;
    }
  });
  return Object.values(grouped);
}

export function getKPITotals(api: GridApi, filteredOnly = false) {
  const totals: Record<string, number> = {};
  const maxImpacts: Record<string, number> = {};
  if (!api || !api.getColumnDefs()) return { totals, maxImpacts };
  const kpiColumns = api.getColumnDefs()!.filter((col) => {
    const field = _.get(col, 'field', '');
    return isKPIColumnName(field) || isKPIExpectedAnnualColumnName(field);
  });

  const rows: IRowNode[] = [];
  if (filteredOnly) {
    api.forEachNodeAfterFilterAndSort((node) => {
      rows.push(node);
    });
  } else {
    api.forEachNode((node) => {
      rows.push(node);
    });
  }

  kpiColumns.forEach((col) => {
    let max = -1;
    const colId = _.get(col, 'field');
    totals[colId] = rows.reduce((sum, node) => {
      const value = Number(node.data[colId]);
      if (value > max) {
        max = value;
        maxImpacts[colId] = max;
      }
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
  });

  return { totals, maxImpacts };
}

let currencyUnitsCache: string[] = quantifyUnits.Currency;
let currencyUnitsInitializationPromise: Promise<void> | undefined;

export function initializeCurrencyUnits() {
  if (currencyUnitsInitializationPromise) {
    return currencyUnitsInitializationPromise;
  }

  currencyUnitsInitializationPromise = getSupportedUnits()
    .then((units) => {
      if (units?.currency?.length) {
        currencyUnitsCache = units.currency;
      }
    })
    .catch(() => undefined);

  return currencyUnitsInitializationPromise;
}

export function getValueFormatter(unit: string, decimalPlaces?: number) {
  if (currencyUnitsCache.includes(unit)) {
    return (value: number, unit: string) => formatNumberAsValue(value, unit, decimalPlaces);
  } else {
    return (value: number, unit: string) => {
      if (!isFinite(value)) return '-';

      // If decimalPlaces is explicitly provided, honor it for all numbers
      if (decimalPlaces !== undefined && decimalPlaces !== null) {
        const formatted = value.toFixed(decimalPlaces);
        // Add comma formatting if >= 1000
        if (Math.abs(value) >= 1000) {
          const [intPart, decPart] = formatted.split('.');
          const withCommas = new Intl.NumberFormat(undefined).format(parseInt(intPart, 10));
          return `${decPart !== undefined ? `${withCommas}.${decPart}` : withCommas} ${unit}`;
        }
        return `${formatted} ${unit}`;
      }

      // If decimalPlaces not specified, use default formatting (round large numbers)
      if (Math.abs(value) >= 1000) {
        return `${new Intl.NumberFormat(undefined).format(Math.round(value))} ${unit}`;
      }
      return `${Math.round(value)} ${unit}`;
    };
  }
}

export function applyColumnConfiguration(api: GridApi, appliedColumnConfiguration?: Record<string, ColumnConfig>) {
  if (!appliedColumnConfiguration || Object.keys(appliedColumnConfiguration).length === 0) return;

  const columnStates: ColumnState[] = Object.entries(appliedColumnConfiguration)
    .map(([colId, cfg]) => {
      const column = api.getColumn(colId);
      return column
        ? {
            colId,
            ...(cfg.visible !== undefined && { hide: !cfg.visible }),
            ...(cfg.width !== undefined && { width: cfg.width }),
          }
        : null;
    })
    .filter(Boolean) as ColumnState[];

  if (columnStates.length === 0) return;

  const ordered = Object.entries(appliedColumnConfiguration)
    .filter(([, cfg]) => cfg.order !== undefined)
    .sort(([, a], [, b]) => (a.order ?? 0) - (b.order ?? 0))
    .map(([colId]) => colId);

  api.applyColumnState({
    state: columnStates,
    applyOrder: ordered.length > 0,
    defaultState: {},
  });

  if (ordered.length) {
    api.moveColumns(ordered, 0);
  }
}

/**
 * Create column configuration from ag-grid columns
 */
export function createColumnConfigFromGrid(columns: Column[]): Record<string, ColumnConfig> {
  const config: Record<string, ColumnConfig> = {};
  columns.forEach((col, index) => {
    config[col.getColId()] = {
      visible: col.isVisible(),
      order: index,
    };
  });
  return config;
}

/**
 * Create column configuration from column definitions
 */
export function createColumnConfigFromDefinitions(
  categories: ImpactReportCategory[],
  showSite: boolean,
  showAssetPath: boolean,
  showLocation: boolean,
  timezone: { name: string },
  kpiColumns: ColumnDefinitionOutputV1[],
  expectedAnnualKpiColumns: ColumnDefinitionOutputV1[] = [],
  expectedAnnualKpiVisibility: Record<string, boolean> = {},
): Record<string, ColumnConfig> {
  const config: Record<string, ColumnConfig> = {};
  getColumnDefinitions(
    categories,
    showSite,
    showAssetPath,
    showLocation,
    timezone,
    kpiColumns,
    expectedAnnualKpiColumns,
    expectedAnnualKpiVisibility,
  ).forEach((colDef, index) => {
    if (colDef.field) {
      config[colDef.field] = {
        visible: !colDef.hide,
        order: index,
        width: colDef.width,
      };
    }
  });
  return config;
}
